import client from "./client";

export interface SignupRequest {
  org_name: string;
  admin_email: string;
  password: string;
  first_name: string;
  last_name: string;
  company_size?: string;
  preferred_slug?: string;
  /** Acceptance of the ToS. Required by the backend — sending ``false``
   *  (or omitting the field entirely) returns a 422. */
  accept_terms?: boolean;
  /** Acceptance of the privacy policy. Same semantics as accept_terms. */
  accept_privacy?: boolean;
  /** Skip the 3-day trial and go straight to Stripe Checkout. Requires
   *  ``plan_key`` to be set. Driven by ``?skip_trial=true`` on /register. */
  skip_trial?: boolean;
  /** Selected paid plan when skipping the trial. */
  plan_key?: "starter" | "growth" | "enterprise";
}

export interface SignupResponse {
  org_id: string;
  org_slug: string;
  user_id: string;
  access_token: string;
  trial_ends_at: string | null;
  /** Set when ``skip_trial`` was True. The SPA redirects to this URL
   *  via ``window.location.href`` so Stripe's hosted Checkout takes
   *  over the next page load. */
  checkout_url?: string | null;
}

export async function signupOrg(data: SignupRequest): Promise<SignupResponse> {
  const res = await client.post<SignupResponse>("/signup/org", data);
  return res.data;
}

export async function checkSlug(
  slug: string
): Promise<{ available: boolean; suggested: string }> {
  const res = await client.post<{ available: boolean; suggested: string }>(
    "/signup/check-slug",
    { slug }
  );
  return res.data;
}

export async function domainLookup(
  email: string
): Promise<{
  org_id: string | null;
  org_name: string | null;
  org_slug: string | null;
}> {
  const res = await client.post("/signup/domain-lookup", { email });
  return res.data;
}

// ─────────────────────────── onboarding wizard ───────────────────────────

export type WizardStep =
  | "company_profile"
  | "departments"
  | "import_employees"
  | "document_templates"
  | "leave_policy"
  | "integrations"
  | "invite_team"
  | "finish"
  // Legacy — kept so the API client still validates old state payloads.
  | "workspace"
  | "connect_integrations";

export interface OnboardingState {
  completed: boolean;
  current_step: WizardStep;
  completed_steps: WizardStep[];
  trial_days_remaining: number | null;
  onboarding_completed_at: string | null;
}

export async function getOnboardingState(): Promise<OnboardingState> {
  const res = await client.get<OnboardingState>("/onboarding/state");
  return res.data;
}

export async function markOnboardingStep(step: WizardStep): Promise<OnboardingState> {
  const res = await client.post<OnboardingState>("/onboarding/step", { step });
  return res.data;
}

export async function resetOnboarding(): Promise<OnboardingState> {
  const res = await client.post<OnboardingState>("/onboarding/reset");
  return res.data;
}

export async function completeOnboarding(): Promise<{ completed_at: string }> {
  const res = await client.post<{ completed_at: string }>(
    "/onboarding/complete"
  );
  return res.data;
}

// ─────────────────────────── domain verification ───────────────────────────

export interface DomainInstructions {
  domain: string;
  host: string;
  type: string;
  value: string;
  ttl: number;
  verified_at: string | null;
}

export async function startDomainVerification(
  domain?: string
): Promise<DomainInstructions> {
  const res = await client.post<DomainInstructions>(
    "/organizations/me/domain/start-verification",
    { domain }
  );
  return res.data;
}

export async function verifyDomain(): Promise<{
  verified: boolean;
  verified_at: string | null;
  message: string;
}> {
  const res = await client.post("/organizations/me/domain/verify");
  return res.data;
}

export async function getDomainStatus(): Promise<DomainInstructions> {
  const res = await client.get<DomainInstructions>(
    "/organizations/me/domain/status"
  );
  return res.data;
}
